import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, Lock, Eye, FileText, UserCheck, Mail, AlertCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "20 January 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-[#42b1c2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-[#42b1c2]" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Important Notice */}
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> This Privacy Policy is provided for informational purposes and describes how Luma Med Global 
                    handles personal data. It does not constitute legal advice. If you have specific legal concerns about data protection, 
                    we recommend consulting with a qualified legal professional.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            
            {/* Introduction */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Introduction
                </h2>
                <p className="text-gray-600 mb-4">
                  Luma Med Global ("we", "our", or "us") is a medically led recruitment platform designed to connect 
                  healthcare professionals with employment opportunities in Saudi Arabia and the Middle East. We are 
                  committed to protecting your privacy and handling your personal data responsibly.
                </p>
                <p className="text-gray-600">
                  This Privacy Policy explains what personal data we collect, why we collect it, how we use and protect it, 
                  and your rights regarding your data. By using our platform, you acknowledge that you have read and understood 
                  this policy.
                </p>
              </CardContent>
            </Card>

            {/* Data We Collect */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Eye className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Personal Data We Collect
                </h2>
                <p className="text-gray-600 mb-4">
                  We collect the following categories of personal data to provide our recruitment services:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Identity & Contact Information</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                      <li>Full name, email address, telephone number</li>
                      <li>Country of residence and nationality</li>
                      <li>Professional title and current role</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Professional & Credential Information</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                      <li>Curriculum Vitae (CV) and cover letters</li>
                      <li>Medical qualifications, certifications, and licences (e.g., GMC, SCFHS)</li>
                      <li>Professional experience, specialisations, and skills</li>
                      <li>Educational background and training records</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Video Interview Data</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                      <li>Video recordings submitted through our interview feature</li>
                      <li>Interview responses and any AI-generated feedback (clearly marked as simulated where applicable)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Account & Usage Data</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                      <li>Account login credentials (passwords are encrypted)</li>
                      <li>Platform usage data, preferences, and interaction history</li>
                      <li>Device information and IP addresses for security purposes</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why We Collect Data */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <UserCheck className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Why We Collect Your Data
                </h2>
                <p className="text-gray-600 mb-4">
                  We collect and process your personal data for the following purposes:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Recruitment Services:</strong> To match your profile with suitable healthcare positions and facilitate introductions to prospective employers.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Credential Verification:</strong> To assist in verifying professional qualifications and licences as part of the recruitment process.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Profile Enhancement:</strong> To provide AI-assisted tools that help improve your application and interview preparedness.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Communication:</strong> To send you relevant job opportunities, platform updates, and respond to your enquiries.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Platform Improvement:</strong> To analyse usage patterns and improve our services (using anonymised data where possible).</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Legal Compliance:</strong> To meet our legal and regulatory obligations.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Lock className="h-5 w-5 text-[#42b1c2] mr-2" />
                  How We Protect Your Data
                </h2>
                <p className="text-gray-600 mb-4">
                  We take the security of your personal data seriously and implement appropriate technical and organisational 
                  measures to protect it:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>All data transmissions are encrypted using industry-standard SSL/TLS protocols</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Passwords are stored using secure one-way hashing algorithms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Access to personal data is restricted to authorised personnel only</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Regular security assessments and monitoring are conducted</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Secure cloud infrastructure with data backup and recovery procedures</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Sharing & Disclosure</h2>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <p className="text-emerald-800 font-medium">
                    We do not sell your personal data to third parties. Your data is never sold for marketing or advertising purposes.
                  </p>
                </div>
                <p className="text-gray-600 mb-4">
                  We may share your data in the following limited circumstances:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>With Employers:</strong> When you express interest in a position or when employers request to view your profile (with appropriate anonymisation where applicable).</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Service Providers:</strong> With trusted third-party providers who assist in operating our platform (e.g., cloud hosting, email services), under strict data protection agreements.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span><strong>Legal Requirements:</strong> When required by law, court order, or to protect our legal rights.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Rights</h2>
                <p className="text-gray-600 mb-4">
                  You have the following rights regarding your personal data:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Right to Access</h3>
                    <p className="text-sm text-gray-600">Request a copy of the personal data we hold about you.</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Right to Rectification</h3>
                    <p className="text-sm text-gray-600">Request correction of inaccurate or incomplete data.</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Right to Erasure</h3>
                    <p className="text-sm text-gray-600">Request deletion of your personal data ("right to be forgotten").</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Right to Restrict Processing</h3>
                    <p className="text-sm text-gray-600">Request that we limit how we use your data.</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Right to Data Portability</h3>
                    <p className="text-sm text-gray-600">Receive your data in a structured, machine-readable format.</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Right to Object</h3>
                    <p className="text-sm text-gray-600">Object to processing based on legitimate interests or for marketing.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Retention</h2>
                <p className="text-gray-600 mb-4">
                  We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, 
                  or as required by law. Specifically:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Active candidate profiles are retained while you maintain an account with us</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Inactive accounts may be archived after 24 months of inactivity</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Upon account deletion request, data is removed within 30 days (except where legal retention is required)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="mb-6 border-[#42b1c2]/30 bg-[#42b1c2]/5">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Mail className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Contact Us
                </h2>
                <p className="text-gray-600 mb-4">
                  If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us:
                </p>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email:</strong> info@lumamedglobal.com</p>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  We aim to respond to all data-related requests within 30 days.
                </p>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Policy Updates</h2>
                <p className="text-gray-600">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
                  We will notify you of any significant changes by posting a notice on our platform or by email. 
                  We encourage you to review this policy periodically.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
